import { useDispatch, useSelector } from 'react-redux';
import { Box, Text } from '@quintoandar-tokko/jopijs';
import useFetchDashboard from '@/components/Dashboard/customHooks/useFetchDashboard';
import PendingCard from '@/components/Dashboard/DashboardPendings/PendingsCard';
import ContactSourceRow from './ContactSourceRow';
import OtherTooltip from '../../OtherTooltip';
import getBaseUrl from './utils';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import styles from './contactSource.module.scss';
import { getOverviewContactsSource } from '@/store/slices/components/dashboard/service';
import DashboardEmptyStateCard from '@/components/Dashboard/DashboardEmptyStateCard';
import UserIcon from '@/public/images/dashboard/user.svg';
import { useTrackOverviewTab } from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';

export default function ContactSource() {
  const dispatch = useDispatch();
  const { contactSource, filters } = useSelector(
    (state) => state.DashboardSlice
  );
  const { ref, isStale, useFetchOnViewport } = useFetchDashboard();
  const track = useTrackOverviewTab();
  const { list, others, loading, hasFetched, others_contacts_count, empty } =
    contactSource;

  useFetchOnViewport(
    (query) => {
      dispatch(getOverviewContactsSource(query));
    },
    [],
    { hasFetched }
  );

  const othersIds = others.map((contact) => contact.id);
  const othersContacts = others_contacts_count;
  const othersPercentage = others.reduce(
    (acc, curr) => acc + curr.percentage,
    0
  );

  const openLeadsView = (idToRedirect) => {
    let url = getBaseUrl(filters);
    url += `&tags_ids=[${idToRedirect}]`;

    window.open(url, '_blank');
  };

  const getTooltipContent = () => {
    const maxVisible = 12;
    const visible = others.slice(0, maxVisible);
    const remaining = others.length - maxVisible;

    return (
      <>
        {visible.map((contact) => (
          <Text key={contact.id}>{`${contact.name}: ${contact.contacts}`}</Text>
        ))}
        {remaining > 0 && (
          <Text>{`${Trans(translations, 'CountMore', remaining)}...`}</Text>
        )}
      </>
    );
  };

  return (
    <PendingCard ref={ref} loading={loading || isStale} id="overview-contacts">
      <PendingCard.Header
        redirectUrl={!empty && getBaseUrl(filters)}
        toolTipText={Trans(translations, 'ShowAll')}
        trackCallback={(element) => track.contactSource(element)}
      >
        {Trans(translations, 'ContactSource')}
      </PendingCard.Header>
      <Box className={styles.contactSource} id="contact-source">
        {empty ? (
          <DashboardEmptyStateCard
            icon={UserIcon}
            title={Trans(translations, 'HereYouWillSeeWhereYourActiveLeads')}
            description={Trans(translations, 'AlwaysAddTheirSourceTag')}
          />
        ) : (
          <>
            {list.map((source) => (
              <ContactSourceRow
                key={source.id}
                id={source.id}
                stats={{
                  name: source.name,
                  contacts: source.contacts,
                  percentage: source.percentage,
                }}
                onClick={() => {
                  openLeadsView(source.id);
                  track.contactSource(TRACK_ELEMENT.ROW);
                }}
                data-testid={`contact-source-row-${source.id}`}
              />
            ))}

            {others.length > 0 && (
              <Box className={styles.othersRow}>
                <ContactSourceRow
                  stats={{
                    name: Trans(translations, 'Others'),
                    contacts: othersContacts,
                    percentage: othersPercentage,
                  }}
                  onClick={() => {
                    openLeadsView(othersIds);
                    track.contactSource(TRACK_ELEMENT.ROW);
                  }}
                  data-testid="contact-source-row-others"
                />

                <OtherTooltip className={styles.tooltip} xAxisPosition="right">
                  {getTooltipContent()}
                </OtherTooltip>
              </Box>
            )}
          </>
        )}
      </Box>
    </PendingCard>
  );
}
